import { Component, OnInit, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { PairingsService } from '../../services/pairings/pairings.service';
import { LoggingService } from '../../services/shared/logging/logging.service';
import { ToastrService } from 'ngx-toastr';
import { PairingSettingsComponent } from './pairing-settings/pairing-settings.component';
import { PairingsAlphaListComponent, PairingsAlphaRow } from './components/pairings-alpha-list/pairings-alpha-list.component';
import { PairingsSwapComponent } from './components/pairings-swap/pairings-swap.component';
import { ModulesGroupsApplicationPairingsBlockDto as BlockDto, ModulesGroupsApplicationPairingsGroupDto as GroupDto, ModulesGroupsApplicationPairingsGroupSlotDto as GroupSlotDto, ModulesGroupsApplicationPairingsRoundTournamentCourseDto as RoundTournamentCourseDto } from '../../api-client/model/models';
import { DialogService } from '../../services/shared/dialog/dialog.service';
import { TournamentService } from '../../services/tournament/tournament.service';
import type { Round } from '../../models';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-pairings',
  standalone: true,
  imports: [CommonModule, FormsModule, PairingSettingsComponent, PairingsAlphaListComponent, PairingsSwapComponent],
  templateUrl: './pairings.component.html',
  styleUrls: ['./pairings.component.scss']
})
export class PairingsComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly pairingsService = inject(PairingsService);
  private readonly logger = inject(LoggingService);
  private readonly toastr = inject(ToastrService);
  private readonly dialogService = inject(DialogService);
  private readonly tournamentService = inject(TournamentService);

  // Local state
  readonly roundId = signal<string | null>(null);
  readonly showSettings = signal<boolean>(false);
  readonly activeTab = signal<'available' | 'alpha' | 'swap'>('available');
  readonly selectedReport = signal<string>('alpha');

  // Service state
  readonly state = this.pairingsService.state;
  readonly selectedCourse = this.pairingsService.selectedCourse;
  readonly pairings = this.pairingsService.pairings;
  readonly blocks = this.pairingsService.blocks;
  readonly availablePlayers = this.pairingsService.availablePlayers;
  readonly settings = this.pairingsService.settings;
  readonly isPairingsPublished = this.pairingsService.isPairingsPublished;
  readonly swapHistory = this.pairingsService.swapHistory;

  readonly reportOptions = [
    { id: 'alpha', label: 'Alpha Pairings Sheet' },
    { id: 'groups', label: 'Pairings by Group' },
    { id: 'starting-times', label: 'Starting Times' }
  ] as const;

  // Computed
  readonly hasBlocks = computed(() => this.blocks().length > 0);
  readonly totalGroups = computed(() => {
    return this.blocks().reduce((sum: number, block: any) => sum + (block.groups?.length ?? 0), 0);
  });
  readonly totalPlayers = computed(() => {
    let count = 0;
    this.blocks().forEach((block: any) => {
      block.groups?.forEach((group: any) => {
        count += group.slots?.filter((s: any) => s.scorecardId).length ?? 0;
      });
    });
    return count;
  });
  readonly slotColumnCount = computed(() => {
    let max = 0;
    this.blocks().forEach((block: BlockDto) => {
      block?.groups?.forEach((group: GroupDto) => {
        const slotCount = group.slots?.length ?? 0;
        if (slotCount > max) {
          max = slotCount;
        }
      });
    });
    return max;
  });
  readonly slotNumbers = computed(() => {
    const count = this.slotColumnCount();
    if (count === 0) {
      return [] as number[];
    }
    return Array.from({ length: count }, (_, index) => index + 1);
  });
  readonly tableColumnCount = computed(() => 5 + this.slotColumnCount());
  readonly availablePlayerCount = computed(() => this.availablePlayers().length);
  readonly hasAvailablePlayers = computed(() => this.availablePlayerCount() > 0);
  readonly selectedRoundTournamentCourseId = computed(() => this.state().selectedRoundTournamentCourseId);
  readonly lastPublishedAt = computed(() => {
    const response = this.pairings();
    const candidate = response?.lastPublished ?? this.settings()?.pairingsLastPublished ?? null;

    if (!candidate) {
      return null;
    }

    const value = candidate instanceof Date ? candidate : new Date(candidate);
    return Number.isNaN(value.getTime()) ? null : value;
  });
  readonly hasPublishTimestamp = computed(() => this.lastPublishedAt() !== null);
  readonly canCopyFromPrevious = computed(() => this.selectedRoundTournamentCourseId() !== null && this.roundId() !== null);
  readonly courseSummaryLabel = computed(() => {
    const selected = this.selectedCourse();
    const rtcId = this.selectedRoundTournamentCourseId();
    if (!selected || rtcId === null) {
      return 'Select a course to load pairings';
    }
    return `${selected.courseName} (RTC ${rtcId})`;
  });
  readonly alphaPlayers = computed<PairingsAlphaRow[]>(() => {
    const rows: PairingsAlphaRow[] = [];

    this.blocks().forEach(block => {
      const blockLabel = block?.number !== undefined && block?.number !== null ? String(block.number) : null;
      const waveLabel = block?.wave ?? null;

      block?.groups?.forEach(group => {
        const groupLabel = group?.number !== undefined && group?.number !== null ? String(group.number) : '—';
        const teeLabel = group?.startTee !== undefined && group?.startTee !== null ? String(group.startTee) : '—';
        const teeTimeDisplay = this.formatTime(group?.startTime);

        group?.slots?.forEach(slot => {
          if (!slot?.playerName) {
            return;
          }

          const identifiers = [
            slot.scorecardId ? `scorecard:${slot.scorecardId}` : null,
            slot.slotId ? `slot:${slot.slotId}` : null,
            group.groupId ? `group:${group.groupId}` : null,
            `row:${groupLabel}-${slot.number ?? ''}`
          ].filter((value): value is string => Boolean(value));

          const id = identifiers.join('|');

          rows.push({
            id,
            playerName: slot.playerName,
            groupLabel,
            teeLabel,
            teeTimeDisplay,
            waveLabel,
            blockLabel,
            classification: slot.playerClassification ?? null
          });
        });
      });
    });

    return rows.sort((a, b) => a.playerName.localeCompare(b.playerName, undefined, { sensitivity: 'base' }));
  });

  ngOnInit(): void {
    this.logger.info('PairingsComponent', 'PairingsComponent initialized');
    this.logger.info('PairingsComponent', 'route.parent:', this.route.parent);

    // Get roundId from parent route params (rounds/:roundId/pairings)
    if (!this.route.parent) {
      this.logger.error('PairingsComponent', 'route.parent is null!');
      return;
    }

    this.route.parent.params.subscribe(params => {
      this.logger.info('PairingsComponent', 'Parent route params:', params);
      const id = params['roundId'];
      this.logger.info('PairingsComponent', 'Extracted roundId from parent route:', id);
      if (id) {
        this.roundId.set(id);
        this.loadCourses(id);
      } else {
        this.logger.warn('PairingsComponent', 'No roundId found in parent route params', params);
      }
    });
  }

  loadCourses(roundId: string): void {
    this.pairingsService.loadCoursesForRound(roundId).subscribe();
  }

  onCourseSelected(roundTournamentCourseId: number): void {
    this.pairingsService.selectCourse(roundTournamentCourseId);
  }

  async onGenerate(): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Generate new pairings? This will replace any existing pairings.',
      { title: 'Generate Pairings', confirmLabel: 'Generate' }
    );

    if (confirmed) {
      this.pairingsService.generatePairings().subscribe();
    }
  }

  async onClear(): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Clear all pairings? This cannot be undone.',
      { title: 'Clear Pairings', confirmLabel: 'Clear All', cancelLabel: 'Keep Pairings' }
    );

    if (confirmed) {
      this.pairingsService.clearPairings(false).subscribe();
    }
  }

  async onClearSlots(): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Clear all player assignments? Group structure will remain.',
      { title: 'Clear Player Assignments', confirmLabel: 'Clear Players', cancelLabel: 'Keep Players' }
    );

    if (confirmed) {
      this.pairingsService.clearPairings(true).subscribe();
    }
  }

  async onPublish(): Promise<void> {
    const confirmed = await this.dialogService.confirm(
      'Publish pairings? This will make them visible to players and officials.',
      { title: 'Publish Pairings', confirmLabel: 'Publish' }
    );

    if (confirmed) {
      this.pairingsService.publishPairings().subscribe();
    }
  }

  onCopy(): void {
    // TODO: Implement copy dialog to select source/target courses
    this.logger.info('PairingsComponent', 'Copy pairings clicked');
  }

  toggleSettings(): void {
    this.showSettings.update(v => !v);
  }

  setActiveTab(tab: 'available' | 'alpha' | 'swap'): void {
    this.activeTab.set(tab);
  }

  onReportChange(reportId: string): void {
    this.selectedReport.set(reportId);
  }

  onPrintSelectedReport(): void {
    const report = this.selectedReport();
    this.logger.info('PairingsComponent', 'Print report requested', { report });
    this.toastr.info('Report generation is coming soon.');
  }

  async onCopyFromPrevious(): Promise<void> {
    if (!this.canCopyFromPrevious()) {
      return;
    }

    const targetRoundTournamentCourseId = this.selectedRoundTournamentCourseId();
    const selectedCourse = this.selectedCourse();
    const currentRoundId = this.roundId();
    const selectedTournament = this.tournamentService.selectedTournamentSignal();
    const selectedRound = this.tournamentService.selectedRoundSignal();

    if (!targetRoundTournamentCourseId || !selectedCourse || !selectedCourse.tournamentCourseId || !currentRoundId) {
      this.toastr.warning('Select a course before copying pairings.');
      return;
    }

    if (!selectedTournament?.tournamentId) {
      this.logger.warn('PairingsComponent', 'No tournament context available for copy operation');
      this.toastr.error('Unable to determine tournament context for copy.');
      return;
    }

    let rounds: Round[];
    try {
      rounds = await firstValueFrom(this.tournamentService.getRounds(selectedTournament.tournamentId));
    } catch (error) {
      this.logger.error('PairingsComponent', 'Failed to load rounds when copying pairings', error);
      this.toastr.error('Unable to determine previous round. Please try again.');
      return;
    }

    const currentRound = rounds.find(r => r.roundId === currentRoundId) ?? rounds.find(r => r.number === selectedRound?.number);
    const currentRoundNumber = currentRound?.number ?? selectedRound?.number ?? null;

    if (!currentRoundNumber) {
      this.logger.warn('PairingsComponent', 'Cannot determine current round number for copy operation', { currentRoundId });
      this.toastr.error('Unable to determine current round number.');
      return;
    }

    const previousRound = rounds
      .filter(r => (r.number ?? 0) < currentRoundNumber)
      .sort((a, b) => (b.number ?? 0) - (a.number ?? 0))[0];

    if (!previousRound?.roundId) {
      this.logger.info('PairingsComponent', 'No previous round found for copy operation', { currentRoundId });
      this.toastr.warning('No previous round is available to copy from.');
      return;
    }

    let previousCourses: RoundTournamentCourseDto[];
    try {
      previousCourses = await firstValueFrom(this.pairingsService.getCoursesSnapshotForRound(previousRound.roundId));
    } catch (error) {
      this.logger.error('PairingsComponent', 'Failed to load previous round courses', error);
      this.toastr.error('Unable to load previous round courses.');
      return;
    }

    const sourceCourse = previousCourses.find(course => course.tournamentCourseId === selectedCourse.tournamentCourseId);

    if (!sourceCourse?.roundTournamentCourseId) {
      this.logger.info('PairingsComponent', 'No matching course found in previous round for copy', {
        previousRoundId: previousRound.roundId,
        tournamentCourseId: selectedCourse.tournamentCourseId
      });
      this.toastr.warning('No matching course found in the previous round to copy from.');
      return;
    }

    const confirmed = await this.dialogService.confirm(
      `Copy pairings from Round ${previousRound.number ?? ''} - ${selectedCourse.courseName ?? 'selected course'} into this round? This will replace any existing pairings.`,
      {
        title: 'Copy Pairings from Previous Round',
        confirmLabel: 'Copy Pairings',
        cancelLabel: 'Cancel'
      }
    );

    if (!confirmed) {
      this.logger.info('PairingsComponent', 'Copy pairings cancelled by user');
      return;
    }

    this.logger.info('PairingsComponent', 'Copy pairings confirmed', {
      sourceRoundTournamentCourseId: sourceCourse.roundTournamentCourseId,
      targetRoundTournamentCourseId,
      previousRoundId: previousRound.roundId,
      currentRoundId
    });

    this.pairingsService.copyPairings(sourceCourse.roundTournamentCourseId, targetRoundTournamentCourseId).subscribe();
  }

  getSlot(group: GroupDto | undefined, slotNumber: number): GroupSlotDto | null {
    if (!group?.slots?.length) {
      return null;
    }

    return group.slots.find(slot => slot.number === slotNumber) ?? null;
  }

  formatTime(value: Date | string | undefined): string {
    if (!value) {
      return '—';
    }

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  }

  formatBlockHeader(block: BlockDto, index: number): string {
    const numberPart = block.number !== undefined && block.number !== null ? `Block ${block.number}` : 'Block';
    const description = block.description ? `: ${block.description}` : '';
    const wave = block.wave ? ` • Wave ${block.wave}` : '';
    const label = `${numberPart}${description}${wave}`.trim();

    if (label.length > 0) {
      return label;
    }

    return `Block ${index + 1}`;
  }

  groupRowClasses(blockIndex: number, groupType?: string | null): Record<string, boolean> {
    const normalized = groupType?.toUpperCase() ?? '';
    return {
      [`block-${(blockIndex % 4) + 1}`]: true,
      'group-row': true,
      'group-a': normalized === 'A',
      'group-b': normalized === 'B',
      'group-c': normalized === 'C'
    };
  }

  displayGroupType(groupType?: string | null): string | null {
    if (!groupType) {
      return null;
    }

    return groupType.toUpperCase();
  }
}
